import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BottomTabNav from '../shared/components/BottomTabNav';
import { formatCurrency, getUserCurrency, formatCurrencySync } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import './CustomerOrderConfirmation.css';

const CustomerOrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached } = useCurrencyCache();
  const orderDetails = location.state?.orderDetails;

  if (!orderDetails) {
    return (
      <div className="App">
        <div className="confirmation-container">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2>Order Not Found</h2>
            <p>Unable to find order details. Please try again.</p>
            <button className="btn-primary" onClick={() => navigate('/menu')}>
              Back to Menu
            </button>
          </div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="confirmation-container">
        <div className="confirmation-content">
          <div className="success-header">
            <div className="success-icon">✅</div>
            <h1>Order Confirmed!</h1>
            <p className="order-id">Order ID: {orderDetails.orderId}</p>
          </div>

          <div className="order-details">
            <h2>Order Details</h2>
            
            <div className="delivery-info">
              <h3>Delivery Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Name:</span>
                  <span className="value">{orderDetails.delivery.name}</span>
                </div>
                <div className="info-item">
                  <span className="label">Phone:</span>
                  <span className="value">{orderDetails.delivery.phone}</span>
                </div>
                <div className="info-item">
                  <span className="label">Address:</span>
                  <span className="value">{orderDetails.delivery.address}</span>
                </div>
                {orderDetails.delivery.city && (
                  <div className="info-item">
                    <span className="label">City:</span>
                    <span className="value">{orderDetails.delivery.city}</span>
                  </div>
                )}
                {orderDetails.delivery.zipCode && (
                  <div className="info-item">
                    <span className="label">ZIP Code:</span>
                    <span className="value">{orderDetails.delivery.zipCode}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="order-items">
              <h3>Order Items</h3>
              <div className="items-list">
                {orderDetails.items.map((item) => (
                  <div key={item.id} className="order-item">
                    <img src={item.image} alt={item.name} className="item-image" />
                    <div className="item-details">
                      <h4 className="item-name">{item.name}</h4>
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                    </div>
                    <span className="item-price">{formatCurrencyCached(item.price * item.quantity, getUserCurrency())}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatCurrencyCached(orderDetails.subtotal || (orderDetails.total / 1.08), getUserCurrency())}</span>
              </div>
              <div className="summary-row">
                <span>Tax ({orderDetails.taxRate || 8}%):</span>
                <span>{formatCurrencyCached(orderDetails.tax || ((orderDetails.total / 1.08) * 0.08), getUserCurrency())}</span>
              </div>
              {orderDetails.deliveryFee && (
                <div className="summary-row">
                  <span>Delivery Fee:</span>
                  <span>{formatCurrencyCached(orderDetails.deliveryFee, getUserCurrency())}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatCurrencyCached(orderDetails.total, getUserCurrency())}</span>
              </div>
            </div>

            <div className="delivery-estimate">
              <h3>Estimated Delivery</h3>
              <p className="estimate-time">{orderDetails.estimatedDelivery}</p>
              <p className="estimate-note">We'll send you updates on your order status</p>
            </div>
          </div>

          <div className="confirmation-actions">
            <button className="btn-primary" onClick={() => navigate('/orders')}>
              View My Orders
            </button>
            <button className="btn-secondary" onClick={() => navigate('/menu')}>
              Order More Food
            </button>
          </div>
        </div>
      </div>
      
      <BottomTabNav />
    </div>
  );
};

export default CustomerOrderConfirmation;

